/**
 * ShellTerminologyProvider — hands the company's word list to the word
 * provider (SS-B7.1, ADR-035).
 *
 * `GET /api/shell/manifest` returns the resolved manifest for the active
 * company, including `terminology`: the shared defaults, then the company's
 * industry template, then the company's own words. This provider fetches it
 * once per company (the query key carries the tenant id, so switching company
 * fetches again) and passes `terminology` to `<TerminologyProvider>`.
 *
 * While the fetch is in flight, when no company is selected yet (login, tenant
 * select) and when it fails, the provider gets an empty map, so every screen
 * shows the shared defaults from labels.ts. A failed fetch never blocks the
 * page.
 *
 * Must mount under `ApiClientProvider` (it reads the shared API client) and
 * under a `QueryClientProvider` (main.tsx mounts one above the router).
 */

import { useAuthStore } from "@ken/auth";
import { useApiClient, type Client, type KenApiPaths } from "@ken/api-client";
import { TerminologyProvider, type TermMap } from "@ken/terminology";
import { useQuery } from "@tanstack/react-query";
import type { ReactElement, ReactNode } from "react";

const EMPTY_TERMS: TermMap = {};

interface ShellManifestWords {
  terminology?: Record<string, unknown> | null;
}

type AnyClient = {
  GET: (path: string) => Promise<{ data?: unknown; response: Response }>;
};

/** Keep only real words; a blank or non-string value is ignored, so the
 *  shared default shows (the server's own rule, SS-B7.6). */
function wordsOf(payload: ShellManifestWords | undefined): TermMap {
  const raw = payload?.terminology;
  if (!raw || typeof raw !== "object") return EMPTY_TERMS;
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (typeof value === "string" && value.trim() !== "") out[key] = value;
  }
  return out as TermMap;
}

async function fetchShellWords(client: Client<KenApiPaths>): Promise<TermMap> {
  const res = await (client as unknown as AnyClient).GET("/api/shell/manifest");
  if (!res.response.ok) {
    throw new Error(`couldn't load the company's words (status ${res.response.status})`);
  }
  return wordsOf(res.data as ShellManifestWords | undefined);
}

/** The active company's word list; empty (so defaults) while loading or on failure. */
export function useShellTerminology(): TermMap {
  const tenantId = useAuthStore((s) => s.session?.tenantId);
  const client = useApiClient();
  const query = useQuery({
    queryKey: ["shell", "manifest", "terminology", tenantId],
    queryFn: () => fetchShellWords(client),
    enabled: typeof tenantId === "string" && tenantId.length > 0,
    staleTime: 5 * 60 * 1000,
  });
  return query.data ?? EMPTY_TERMS;
}

export function ShellTerminologyProvider({ children }: { children: ReactNode }): ReactElement {
  const terms = useShellTerminology();
  return <TerminologyProvider terms={terms}>{children}</TerminologyProvider>;
}
